import { useCallback, useEffect, useState } from "react";
import {
  AlertCircle,
  AlertTriangle,
  CheckCircle2,
  Copy,
  Loader2,
  MessageCircle,
  RefreshCw,
  Save,
  ShieldCheck,
  Wallet,
} from "lucide-react";
import { PageHeader } from "../components/PageHeader";
import { StatusBadge } from "../components/StatusBadge";
import { Toast, type ToastMessage } from "../components/Toast";
import {
  api,
  ApiRequestError,
  type ConnectionResult,
  type IntegrationsResponse,
} from "../lib/api";
import { formatDateTime } from "../lib/format";

type WebqrisForm = {
  apiBaseUrl: string;
  apiToken: string;
  webhookSecret: string;
  webhookUrlConfigured: string;
};

type WaForm = {
  apiBaseUrl: string;
  apiKey: string;
  webhookUrl: string;
  webhookSecret: string;
};

const emptyWebqris: WebqrisForm = {
  apiBaseUrl: "https://webqris.com",
  apiToken: "",
  webhookSecret: "",
  webhookUrlConfigured: "",
};

const emptyWa: WaForm = {
  apiBaseUrl: "https://app.kalimashada.id",
  apiKey: "",
  webhookUrl: "",
  webhookSecret: "",
};

export function IntegrationsPage() {
  const [data, setData] = useState<IntegrationsResponse | null>(null);
  const [webqris, setWebqris] = useState<WebqrisForm>(emptyWebqris);
  const [wa, setWa] = useState<WaForm>(emptyWa);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState<"webqris" | "wa" | null>(null);
  const [testing, setTesting] = useState<"webqris" | "wa" | null>(null);
  const [testPhone, setTestPhone] = useState("");
  const [testMessage, setTestMessage] = useState("");
  const [sendingTest, setSendingTest] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<ToastMessage | null>(null);

  const closeToast = useCallback(() => setToast(null), []);

  const load = useCallback(async () => {
    try {
      const result = await api<IntegrationsResponse>("/api/owner/integrations");
      setData(result);
      setWebqris({
        apiBaseUrl: result.webqris?.apiBaseUrl ?? "https://webqris.com",
        apiToken: "",
        webhookSecret: "",
        webhookUrlConfigured: result.webqris?.webhookUrlConfigured ?? "",
      });
      setWa({
        apiBaseUrl: result.whatsapp?.apiBaseUrl ?? "https://app.kalimashada.id",
        apiKey: "",
        webhookUrl: result.whatsapp?.webhookUrl ?? "",
        webhookSecret: "",
      });
    } catch (requestError) {
      setError(
        requestError instanceof ApiRequestError
          ? requestError.message
          : "Tidak dapat memuat pengaturan integrasi.",
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  async function saveWebqris() {
    setSaving("webqris");
    setError(null);
    try {
      await api("/api/owner/integrations/webqris", {
        method: "PUT",
        body: JSON.stringify({
          api_base_url: webqris.apiBaseUrl,
          api_token: webqris.apiToken || undefined,
          webhook_secret: webqris.webhookSecret || undefined,
          webhook_url_configured: webqris.webhookUrlConfigured || undefined,
        }),
      });
      await load();
      setToast({
        tone: "success",
        title: "Kredensial WebQRIS tersimpan",
        message: "Jalankan tes koneksi untuk memverifikasi token.",
      });
    } catch (requestError) {
      setToast({
        tone: "error",
        title: "Gagal menyimpan WebQRIS",
        message:
          requestError instanceof ApiRequestError
            ? requestError.message
            : "Terjadi kesalahan.",
      });
    } finally {
      setSaving(null);
    }
  }

  async function saveWa() {
    setSaving("wa");
    setError(null);
    try {
      await api("/api/owner/integrations/wa", {
        method: "PUT",
        body: JSON.stringify({
          api_base_url: wa.apiBaseUrl,
          api_key: wa.apiKey || undefined,
          webhook_url: wa.webhookUrl || undefined,
          webhook_secret: wa.webhookSecret || undefined,
        }),
      });
      await load();
      setToast({
        tone: "success",
        title: "Kredensial WA tersimpan",
        message: "Jalankan tes koneksi untuk memverifikasi device.",
      });
    } catch (requestError) {
      setToast({
        tone: "error",
        title: "Gagal menyimpan WA Gateway",
        message:
          requestError instanceof ApiRequestError
            ? requestError.message
            : "Terjadi kesalahan.",
      });
    } finally {
      setSaving(null);
    }
  }

  async function testConnection(kind: "webqris" | "wa") {
    setTesting(kind);
    try {
      const result = await api<ConnectionResult>(
        `/api/owner/integrations/${kind}/test`,
        { method: "POST" },
      );
      await load();
      setToast({
        tone: result.status === "active" ? "success" : "error",
        title:
          result.status === "active"
            ? "Koneksi berhasil"
            : "Koneksi perlu diperiksa",
        message: result.message,
      });
    } catch (requestError) {
      setToast({
        tone: "error",
        title: "Tes koneksi gagal",
        message:
          requestError instanceof ApiRequestError
            ? requestError.message
            : "Tidak dapat menghubungi server.",
      });
    } finally {
      setTesting(null);
    }
  }

  async function sendWaTest() {
    if (!testPhone.trim()) {
      setToast({
        tone: "error",
        title: "Nomor tujuan belum diisi",
        message: "Masukkan nomor WhatsApp penerima pesan uji.",
      });
      return;
    }

    setSendingTest(true);
    try {
      const result = await api<{
        sent: boolean;
        batchId: string | null;
        queued: number;
        phone: string;
      }>("/api/owner/integrations/wa/test-send", {
        method: "POST",
        body: JSON.stringify({
          phone: testPhone.trim(),
          message: testMessage.trim() || undefined,
        }),
      });
      await load();
      setToast({
        tone: "success",
        title: "Pesan uji masuk antrean",
        message: `${result.queued} pesan untuk ${result.phone}${
          result.batchId ? ` · batch ${result.batchId}` : ""
        }`,
      });
    } catch (requestError) {
      setToast({
        tone: "error",
        title: "Gagal mengirim pesan uji",
        message:
          requestError instanceof ApiRequestError
            ? requestError.message
            : "Tidak dapat menghubungi WA Gateway.",
      });
    } finally {
      setSendingTest(false);
    }
  }

  async function copyWebhookUrl() {
    const url = data?.webqris?.webhookUrl;
    if (!url) return;
    try {
      await navigator.clipboard.writeText(url);
      setToast({
        tone: "success",
        title: "Webhook URL disalin",
        message: "Tempel di pengaturan merchant WebQRIS.",
      });
    } catch {
      setToast({
        tone: "error",
        title: "Gagal menyalin",
        message: "Salin URL secara manual dari kolom webhook.",
      });
    }
  }

  if (loading) {
    return (
      <div className="page-stack">
        <PageHeader
          eyebrow="Pengaturan"
          title="Integrasi"
          description="Hubungkan pembayaran dan notifikasi ke workspace Anda."
        />
        <div className="integration-grid">
          <div className="card card--pad">
            <span className="skeleton skeleton--title" />
            <span className="skeleton skeleton--line" />
            <span className="skeleton skeleton--line" />
            <span className="skeleton skeleton--block" />
          </div>
          <div className="card card--pad">
            <span className="skeleton skeleton--title" />
            <span className="skeleton skeleton--line" />
            <span className="skeleton skeleton--line" />
            <span className="skeleton skeleton--block" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page-stack">
      <PageHeader
        eyebrow="Pengaturan"
        title="Integrasi"
        description="Hubungkan WebQRIS dan WhatsApp Gateway. Kredensial hanya dapat diubah oleh owner."
        actions={
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => void load()}
          >
            <RefreshCw size={16} />
            Muat ulang
          </button>
        }
      />

      {error ? (
        <div className="alert alert--danger" role="alert">
          <AlertCircle size={17} />
          <span>{error}</span>
        </div>
      ) : null}

      <div className="security-banner">
        <ShieldCheck size={20} />
        <div>
          <strong>Secret disimpan terenkripsi</strong>
          <span>
            Token dan webhook secret tidak pernah dikirim kembali ke browser
            setelah disimpan. Nilai hanya ditampilkan dalam bentuk masking.
          </span>
        </div>
      </div>

      <div className="integration-grid">
        <section className="card card--pad integration-card">
          <div className="integration-card__head">
            <div className="integration-card__identity">
              <span className="integration-card__icon">
                <Wallet size={20} />
              </span>
              <div>
                <p className="eyebrow">Pembayaran</p>
                <h2>WebQRIS</h2>
                <p>QRIS dinamis untuk pesanan pelanggan.</p>
              </div>
            </div>
            <StatusBadge status={data?.webqris?.status ?? "not_configured"} />
          </div>

          <div className="form-grid">
            <label className="field field--full">
              <span>API base URL</span>
              <input
                className="input"
                value={webqris.apiBaseUrl}
                onChange={(event) =>
                  setWebqris((current) => ({
                    ...current,
                    apiBaseUrl: event.target.value,
                  }))
                }
                placeholder="https://webqris.com"
                inputMode="url"
              />
            </label>

            <label className="field">
              <span>API token</span>
              <input
                className="input"
                type="password"
                value={webqris.apiToken}
                onChange={(event) =>
                  setWebqris((current) => ({
                    ...current,
                    apiToken: event.target.value,
                  }))
                }
                placeholder={
                  data?.webqris?.hasApiToken
                    ? "Sudah tersimpan — isi untuk mengganti"
                    : "Tempel API token"
                }
                autoComplete="new-password"
              />
            </label>

            <label className="field">
              <span>Webhook secret</span>
              <input
                className="input"
                type="password"
                value={webqris.webhookSecret}
                onChange={(event) =>
                  setWebqris((current) => ({
                    ...current,
                    webhookSecret: event.target.value,
                  }))
                }
                placeholder={
                  data?.webqris?.hasWebhookSecret
                    ? "Sudah tersimpan — isi untuk mengganti"
                    : "Tempel webhook secret"
                }
                autoComplete="new-password"
              />
            </label>

            <label className="field field--full">
              <span>Webhook URL yang dipasang di WebQRIS</span>
              <input
                className="input"
                value={webqris.webhookUrlConfigured}
                onChange={(event) =>
                  setWebqris((current) => ({
                    ...current,
                    webhookUrlConfigured: event.target.value,
                  }))
                }
                placeholder="Tempel URL yang Anda set di merchant"
                inputMode="url"
              />
            </label>
          </div>

          <div className="webhook-panel">
            <div>
              <span className="webhook-panel__label">
                Webhook URL dari Cafe System Control
              </span>
              <code>
                {data?.webqris?.webhookUrl ??
                  "Simpan kredensial untuk membuat URL webhook."}
              </code>
            </div>
            <button
              type="button"
              className="btn btn-secondary btn--sm"
              onClick={() => void copyWebhookUrl()}
              disabled={!data?.webqris?.webhookUrl}
            >
              <Copy size={14} />
              Salin
            </button>
          </div>

          {data?.webqris?.webhookUrlMatches === false ? (
            <div className="alert alert--warning">
              <AlertTriangle size={17} />
              <span>
                URL yang dipasang di WebQRIS berbeda dari URL aplikasi. Perbarui
                webhook merchant agar pembayaran masuk otomatis.
              </span>
            </div>
          ) : null}

          <div className="integration-meta">
            <span>
              Terakhir diverifikasi:{" "}
              <strong>{formatDateTime(data?.webqris?.lastVerifiedAt)}</strong>
            </span>
            {data?.webqris?.lastError ? (
              <span className="integration-meta__error">
                {data.webqris.lastError}
              </span>
            ) : null}
          </div>

          <div className="card-actions">
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => void saveWebqris()}
              disabled={saving === "webqris"}
            >
              {saving === "webqris" ? (
                <Loader2 className="spin" size={16} />
              ) : (
                <Save size={16} />
              )}
              Simpan WebQRIS
            </button>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => void testConnection("webqris")}
              disabled={testing === "webqris" || !data?.webqris?.hasApiToken}
            >
              {testing === "webqris" ? (
                <Loader2 className="spin" size={16} />
              ) : (
                <CheckCircle2 size={16} />
              )}
              Tes koneksi
            </button>
          </div>
        </section>

        <section className="card card--pad integration-card">
          <div className="integration-card__head">
            <div className="integration-card__identity">
              <span className="integration-card__icon integration-card__icon--green">
                <MessageCircle size={20} />
              </span>
              <div>
                <p className="eyebrow">Notifikasi</p>
                <h2>WhatsApp Gateway</h2>
                <p>Struk dan update status untuk pelanggan.</p>
              </div>
            </div>
            <StatusBadge status={data?.whatsapp?.status ?? "not_configured"} />
          </div>

          <div className="form-grid">
            <label className="field field--full">
              <span>API base URL</span>
              <input
                className="input"
                value={wa.apiBaseUrl}
                onChange={(event) =>
                  setWa((current) => ({
                    ...current,
                    apiBaseUrl: event.target.value,
                  }))
                }
                placeholder="https://app.kalimashada.id"
                inputMode="url"
              />
            </label>

            <label className="field field--full">
              <span>API key device</span>
              <input
                className="input"
                type="password"
                value={wa.apiKey}
                onChange={(event) =>
                  setWa((current) => ({
                    ...current,
                    apiKey: event.target.value,
                  }))
                }
                placeholder={
                  data?.whatsapp?.hasApiKey
                    ? "Sudah tersimpan — isi untuk mengganti"
                    : "Tempel API key device"
                }
                autoComplete="new-password"
              />
            </label>

            <label className="field field--full">
              <span>Webhook URL (opsional)</span>
              <input
                className="input"
                value={wa.webhookUrl}
                onChange={(event) =>
                  setWa((current) => ({
                    ...current,
                    webhookUrl: event.target.value,
                  }))
                }
                placeholder="https://cafe.kalimashada.id/api/webhooks/wa/..."
                inputMode="url"
                autoComplete="off"
              />
            </label>

            <label className="field field--full">
              <span>Webhook secret (opsional)</span>
              <input
                className="input"
                type="password"
                value={wa.webhookSecret}
                onChange={(event) =>
                  setWa((current) => ({
                    ...current,
                    webhookSecret: event.target.value,
                  }))
                }
                placeholder={
                  data?.whatsapp?.hasWebhookSecret
                    ? "Sudah tersimpan — isi untuk mengganti"
                    : "Tempel webhook secret WA Gateway"
                }
                autoComplete="new-password"
              />
            </label>
          </div>

          <div className="integration-meta">
            <span>
              Terakhir diverifikasi:{" "}
              <strong>{formatDateTime(data?.whatsapp?.lastVerifiedAt)}</strong>
            </span>
            {data?.whatsapp?.lastError ? (
              <span className="integration-meta__error">
                {data.whatsapp.lastError}
              </span>
            ) : null}
          </div>

          <div className="card-actions">
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => void saveWa()}
              disabled={saving === "wa"}
            >
              {saving === "wa" ? (
                <Loader2 className="spin" size={16} />
              ) : (
                <Save size={16} />
              )}
              Simpan WA
            </button>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => void testConnection("wa")}
              disabled={testing === "wa" || !data?.whatsapp?.hasApiKey}
            >
              {testing === "wa" ? (
                <Loader2 className="spin" size={16} />
              ) : (
                <CheckCircle2 size={16} />
              )}
              Tes koneksi
            </button>
          </div>

          <div className="integration-test-send">
            <div className="section-heading section-heading--compact">
              <div>
                <p className="eyebrow">Verifikasi</p>
                <h3>Kirim pesan uji</h3>
                <p className="section-subtitle">
                  Pesan benar-benar dikirim lewat device WA tenant. Gunakan
                  nomor sendiri saat menguji.
                </p>
              </div>
            </div>
            <div className="form-grid">
              <label className="field">
                <span>Nomor tujuan</span>
                <input
                  className="input"
                  inputMode="tel"
                  value={testPhone}
                  onChange={(event) => setTestPhone(event.target.value)}
                  placeholder="08xxxxxxxxxx"
                />
              </label>
              <label className="field">
                <span>Pesan (opsional)</span>
                <input
                  className="input"
                  value={testMessage}
                  onChange={(event) => setTestMessage(event.target.value)}
                  placeholder="Pesan uji dari Cafe System Control"
                />
              </label>
            </div>
            <div className="card-actions">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => void sendWaTest()}
                disabled={
                  sendingTest ||
                  !data?.whatsapp?.hasApiKey ||
                  !testPhone.trim()
                }
              >
                {sendingTest ? (
                  <Loader2 className="spin" size={16} />
                ) : (
                  <MessageCircle size={16} />
                )}
                Kirim pesan uji
              </button>
            </div>
          </div>
        </section>
      </div>

      {toast ? <Toast toast={toast} onClose={closeToast} /> : null}
    </div>
  );
}
